import { CrossroadsEntityType } from '@/api/types'
import {
  ICellEditorParams,
  type SuppressKeyboardEventParams,
} from 'ag-grid-community'

/** Suppress Enter and arrow keys during editing so the dropdown can handle them. */
export const suppressDropdownKeyboardEvent = (
  params: SuppressKeyboardEventParams,
): boolean => {
  if (!params.editing) return false
  const key = params.event.key
  return key === 'Enter' || key === 'ArrowUp' || key === 'ArrowDown'
}
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { MRDEntitySelect } from './MRDEntitySelect'

interface MRDMultiSelectEditorProps extends ICellEditorParams {
  value: string[]
  type?: CrossroadsEntityType | CrossroadsEntityType[]
  forcedSearchTerm?: string
  onChange?: (value: string[]) => void
  showTypeSelector?: boolean
  /** Whether to allow multiple selections. Defaults to true. */
  multi?: boolean
}

/**
 * MultiSelectEditor - ag-grid cell editor wrapper around MRDEntitySelect.
 * Provides getValue/isCancelBeforeStart/isCancelAfterEnd for ag-grid integration.
 */
export const MRDMultiSelectEditor = forwardRef<
  {
    getValue: () => string[] | string | null
    isCancelBeforeStart: () => boolean
    isCancelAfterEnd: () => boolean
  },
  MRDMultiSelectEditorProps
>((props, ref) => {
  const {
    value: defaultValue,
    type: defaultType,
    onChange,
    showTypeSelector,
    context,
    multi = true,
  } = props

  const selectedObject = context?.selectedObject?.entity_type
  const cellWidth = props.column?.getActualWidth()
  const cellHeight = props.eParentOfValue?.parentElement?.offsetHeight

  const [value, setValue] = useState<string[]>(
    multi ? defaultValue : (defaultValue?.slice(0, 1) ?? []),
  )
  const valueRef = useRef(value)

  useImperativeHandle(ref, () => ({
    getValue: () => {
      if (multi) return valueRef.current
      return valueRef.current[0] || null
    },
    isCancelBeforeStart: () => false,
    isCancelAfterEnd: () => false,
  }))

  const handleChange = useCallback(
    (newValue: string | string[] | undefined) => {
      const arrayValue = Array.isArray(newValue)
        ? newValue
        : newValue
          ? [newValue]
          : []
      setValue(arrayValue)
      valueRef.current = arrayValue
      onChange?.(arrayValue)
      // Auto-close editor after selection in single-select mode
      if (!multi) {
        props.api.stopEditing()
      }
    },
    [multi, onChange, props.api],
  )

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <MRDEntitySelect
        value={multi ? value : value[0]}
        onChange={handleChange}
        entityType={
          Array.isArray(defaultType)
            ? defaultType
            : defaultType || selectedObject || 'Product'
        }
        multi={multi}
        showTypeSelector={showTypeSelector}
        autoFocus
        defaultOpen
        forcedSearchTerm={props.forcedSearchTerm}
        width={cellWidth}
        height={cellHeight}
      />
    </div>
  )
})
